import { AddIcon, CheckIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  chakra,
  Checkbox,
  Divider,
  Flex,
  HStack,
  Icon,
  ResponsiveValue,
  Stack,
  Text,
  useCheckbox,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'
import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiDownload } from 'react-icons/bi'
import { WORK_ORDER } from '../workOrder.i18n'
import {
  SWOProject,
  useActionsShowDecision,
  useFieldEnableDecision,
  useGetLineItemsColumn,
} from './assignedItems.utils'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { ProjectWorkOrderType } from 'types/project.type'
import { RiErrorWarningLine } from 'react-icons/ri'
import { datePickerFormat } from 'utils/date-time-utils'
import { TableContextProvider } from 'components/table-refactored/table-context'
import Table from 'components/table-refactored/table'

export const CustomCheckBox = props => {
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } = useCheckbox(props)

  return (
    <chakra.label
      display="flex"
      alignItems="center"
      gridColumnGap={3}
      w="125px"
      h="34px"
      rounded="8px"
      cursor={!state.isDisabled ? 'pointer' : 'default'}
      {...htmlProps}
    >
      <input {...getInputProps()} hidden id={props.id} />
      <HStack
        ml="2"
        justifyContent="center"
        border={state.isChecked ? '2px solid #48BB78' : '2px solid #E2E8F0'}
        rounded="2px"
        w={4}
        id={props.id}
        h={4}
        data-testid={props?.testid}
        {...getCheckboxProps()}
        onChange={e => {
          if (!state.isDisabled) {
            props.onChange(e)
          } else return
        }}
      >
        {state.isChecked && <Icon as={CheckIcon} boxSize="3" color={state.isChecked ? '#48BB78' : '#A0AEC0'} />}
      </HStack>
      <Text mr="2" color={state.isChecked ? '#2AB450' : '#A0AEC0'} {...getLabelProps()}>
        {props.text}
      </Text>
    </chakra.label>
  )
}

type AssignedItemType = {
  isLoadingLineItems?: boolean
  assignedItemsArray: FieldValues

  formControl: UseFormReturn
  isAssignmentAllowed: boolean
  swoProject?: SWOProject
  downloadPdf?: () => void
  workOrder: ProjectWorkOrderType | null
  onDocumentModalOpen?: (e, index) => void
  itemsListWithImages?: number[]
}

const AssignedItems = (props: AssignedItemType) => {
  const {
    isLoadingLineItems,
    assignedItemsArray,
    formControl,
    isAssignmentAllowed,
    downloadPdf,
    workOrder,
    onDocumentModalOpen,
    itemsListWithImages,
  } = props
  const { control, register, getValues, setValue } = formControl
  const { t } = useTranslation()
  const { fields: assignedItems, append: addAssignedItem } = assignedItemsArray
  const VENDOR_UNASSIGNED = 100001
  const [overflowXVal, setOverflowXVal] = useState<ResponsiveValue<any> | undefined>('auto')

  const values = getValues()

  const lineItems = useWatch({ name: 'assignedItems', control })
  const markAllCompleted = lineItems?.length > 0 && lineItems.every(l => l.isCompleted)
  const checkMarkAllVerified = lineItems?.length > 0 && lineItems.every(l => l.isCompleted && l.isVerified)

  // HN-PSWOA-6725| Below useEffect triggers when complete checkboxes events are triggered ... when all complete checkboxes are checked then complete by vendor date is autofilled by current date
  useEffect(() => {
    const allCompleted = lineItems?.length > 0 && lineItems?.every(l => l.isCompleted); 
    if (allCompleted) {
      setValue('workOrderDateCompleted', datePickerFormat(new Date()))
    }
  }, [markAllCompleted])

  // HN-PSWOA-6725| Below useEffect triggers when verified checkboxes events are triggered ... when all verified checkboxes are checked then verified date is autofilled by current date
  useEffect(() => {
    const allVerified = lineItems?.length > 0 && lineItems?.every(l => l.isCompleted && l.isVerified)
    if (allVerified) {
      setValue('workOrderDateVerified', datePickerFormat(new Date()))
      setMarkAllVerified(true)
    } else {
      setMarkAllVerified(false)
    }
  }, [checkMarkAllVerified])
  
  useEffect(() => {
    const isAnyItemComplete = lineItems?.length > 0 && lineItems?.some(l => l.isCompleted)
    if (!isAnyItemComplete) {
      setMarkAllVerified(false)
    }
  }, [markAllCompleted, checkMarkAllVerified])

  const { showPriceCheckBox, notifyVendorCheckBox, showMarkAllIsVerified, showMarkAllIsCompleted } =
    useActionsShowDecision({ workOrder })

  const { statusEnabled, verificationEnabled } = useFieldEnableDecision({ workOrder, lineItems })
  const { isVendor } = useUserRolesSelector()
  const [markAllVerified, setMarkAllVerified] = useState<boolean>()
  const allowEdit = !isVendor

  const ASSIGNED_ITEMS_COLUMNS = useGetLineItemsColumn({
    formControl,
    allowEdit,
    assignedItemsArray,
    workOrder,
    onDocumentModalOpen,
    itemsListWithImages,
  })

  const handleOnDragEnd = useCallback(
    result => {
      if (!result.destination) return

      const items = Array.from(values.assignedItems)
      const {
        source: { index: sourceIndex },
        destination: { index: destinationIndex },
      } = result

      const [reorderedItem] = items.splice(sourceIndex, 1)
      items.splice(destinationIndex, 0, reorderedItem)

      setValue('assignedItems', items)
      setOverflowXVal('auto')
    },
    [values?.assignedItems],
  )

  const handleOnDragStart = useCallback(result => {
    setOverflowXVal('hidden')
  }, [])

  const addInlineAssignedItem = () => {
    addAssignedItem({
      sku: '',
      location: '',
      productName: '',
      description: '',
      quantity: '1',
      unitPrice: 0,
      totalPrice: 0,
      action: 'new',
      id: '',
      clientAmount: 0,
      vendorAmount: 0,
      price: 0,
      profit: 0,
    })
  }

  /* HN-PSWOA-6825| Below useEffect triggers up if either mark all verified or completed checkboxes are changed. 
  The function loops over assigned items and checks if completed checkbox has become false then set the adjacent verified checkbox plus the "Mark all verified" checkbox to false */
  useEffect(() => {
    let uncheckMarkAllVerifiedCheckbox = false;
    assignedItems.forEach((item, index) => {
      if (!values?.assignedItems?.[index]?.isCompleted) {
        setValue(`assignedItems.${index}.isVerified`, values?.assignedItems?.[index]?.isCompleted)
        uncheckMarkAllVerifiedCheckbox = true;
      }
    })
    if (uncheckMarkAllVerifiedCheckbox) setMarkAllVerified(false);
  }, [markAllVerified, markAllCompleted])

  return (
    <Box>
      <>
        <Stack
          mb={0}
          direction="row"
          flexWrap="wrap"
          justifyContent={{ base: 'center', md: 'space-between' }}
          experimental_spaceY={{ base: '16px', sm: '0px' }}
          sx={{
            '@media only screen and (min-width: 415px)': {
              experimental_spaceY: '0',
            },
          }}
        >
          <Flex flexDir={'row'} w="100%" justifyContent={'space-between'}>
            <HStack
              mb={5}
              alignItems={{ base: 'start', sm: 'center' }}
              justifyContent={'start'}
              experimental_spaceY={{ base: '16px', sm: '0px' }}
              sx={{
                '@media only screen and (min-width: 415px)': {
                  experimental_spaceY: '0',
                },
              }}
              ml={1}
              flexWrap="wrap"
              w={{ base: '100%', lg: '100%' }}
            >
              <Text fontWeight={500} color="#4A5568" fontSize={'16px'} whiteSpace="nowrap" mt="3px">
                {t(`${WORK_ORDER}.assignedLineItems`)}
              </Text>
              {isAssignmentAllowed && (
                <>
                  <Box pl="2" pr="1" mt="3px" display={{ base: 'none', sm: 'unset' }}>
                    <Divider size="lg" orientation="vertical" h="25px" />
                  </Box>
                  <Button
                    fontWeight={500}
                    variant="ghost"
                    data-testid="addItemsBtn"
                    colorScheme="brand"
                    onClick={addInlineAssignedItem}
                    position="relative"
                    left="-10px"
                    top="1px"
                    leftIcon={<Icon as={AddIcon} boxSize={2} />}
                  >
                    {t(`${WORK_ORDER}.addNewItem`)}
                  </Button>
                </>
              )}
            </HStack>
            <Box>
              <HStack
                experimental_spaceX={{ base: '5px', lg: '16px' }}
                experimental_spaceY={{ base: '16px', sm: '0' }}
                sx={{
                  '@media only screen and (min-width: 415px)': {
                    experimental_spaceY: '0',
                  },
                }}
                alignItems="center"
                justifyContent={{ base: 'center', sm: 'space-between', lg: 'end' }}
                w={{ base: '100%', lg: 'unset' }}
                flexWrap={{ base: 'wrap', lg: 'unset' }}
              >
                {showPriceCheckBox && (
                  <Checkbox
                    variant={'outLinePrimary'}
                    data-testid="showPriceCheckBox"
                    size="md"
                    {...register('showPrice')}
                  >
                    {t(`${WORK_ORDER}.showPrice`)}
                  </Checkbox>
                )}
                {notifyVendorCheckBox && (
                  <Checkbox
                    defaultChecked
                    variant={'outLinePrimary'}
                    data-testid="notifyVendorCheckBox"
                    size="md"
                    {...register('notifyVendor')}
                  >
                    {t(`${WORK_ORDER}.sendNotification`)}
                  </Checkbox>
                )}
                {showMarkAllIsCompleted && (
                  <Checkbox
                    data-testid="showMarkAllIsComplete"
                    size="md"
                    //{...register('markAllCompleted')}
                    disabled={(!statusEnabled)}
                    isChecked={markAllCompleted}
                    variant={'outLinePrimary'}
                    onChange={e => {
                      assignedItems.forEach((item, index) => {
                        setValue(`assignedItems.${index}.isCompleted`, e.currentTarget.checked)
                      })
                      //setValue('markAllCompleted', markAllCompleted)
                    }}
                    whiteSpace="nowrap"
                   
                  >
                    {t(`${WORK_ORDER}.markAllCompleted`)}
                  </Checkbox>
                )}
                {showMarkAllIsVerified && (
                  <HStack spacing={'2px'}>
                    <Checkbox
                      data-testid="showMarkAllIsVerified"
                      size="md"
                      variant={'outLinePrimary'}
                      disabled={!verificationEnabled}
                      isChecked={markAllVerified}
                      //{...register('markAllVerified')}
                      onChange={e => {
                        assignedItems.forEach((item, index) => {

                          if (values?.assignedItems?.[index]?.isCompleted) {
                            setValue(`assignedItems.${index}.isVerified`, e.currentTarget.checked)
                          }
                        })
                        //setValue('markAllVerified', markAllVerified)
                        setMarkAllVerified(e.target.checked)
                      }}
                      whiteSpace="nowrap"
                    >
                      {t(`${WORK_ORDER}.markAllVerified`)}
                    </Checkbox>
                    <Icon
                      as={RiErrorWarningLine}
                      title="Only completed items can be verified"
                      color="#4299E1"
                      boxSize={4}
                    />
                  </HStack>
                )}
                {downloadPdf && (
                  <Button
                    variant="outline"
                    data-testid="downloadPdf"
                    onClick={downloadPdf}
                    colorScheme="darkPrimary"
                    disabled={assignedItems?.length < 1 || workOrder?.vendorId === VENDOR_UNASSIGNED}
                    leftIcon={<Icon as={BiDownload} boxSize={4} />}
                  >
                    {t(`${WORK_ORDER}.downloadPDF`)}
                  </Button>
                )}
              </HStack>
            </Box>
          </Flex>
        </Stack>

        <Box width="100%" overflowX={overflowXVal} overflowY={'hidden'} borderRadius={7} border="1px solid #CBD5E0">
          <TableContextProvider data={values.assignedItems} columns={ASSIGNED_ITEMS_COLUMNS}>
            <Table
              handleOnDrag={handleOnDragEnd}
              handleOnDragStart={handleOnDragStart}
              isLoading={isLoadingLineItems}
              isEmpty={!isLoadingLineItems && !values.assignedItems?.length}
              isHideFilters={true}
            />
          </TableContextProvider>
        </Box>
      </>
    </Box>
  )
}

export default AssignedItems
